import ScoreCardMetric from './ScoreCardMetric.jsx';
import { BarChartCard, DonutChartCard } from './ScoreCardCharts.jsx';

function number(value) {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function percent(value) {
  if (value === null || value === undefined || value === '') return 'N/A';
  return `${Number(value).toFixed(1)}%`;
}

function statValue(item) {
  if (item?.format === 'percent') return percent(item.value);
  if (item?.format === 'hoursPerDay') return item.value === null || item.value === undefined ? 'N/A' : `${Number(item.value).toFixed(1)}h/day`;
  if (item?.format === 'text') return item.displayValue || item.value || 'N/A';
  return number(item?.value).toLocaleString();
}

function thresholdLabel(metricRows, milestoneType) {
  return metricRows?.find((row) => row.milestone_type === milestoneType)?.tat_threshold_label || 'N/A';
}

function toStatusItems(metric, mode, creScope) {
  if (!metric) return [];
  if (mode === 'tat') {
    return [
      { label: 'On-Time', value: number(metric.tat_on_time), tone: 3 },
      { label: 'Late', value: number(metric.tat_late), tone: 2 },
    ].filter((item) => item.value > 0);
  }
  if (!metric.quality_applicable) return [];
  if (creScope) {
    return [
      { label: 'No CRE Error', value: number(metric.quality_passed), tone: 3 },
      { label: `CRE Errors - ${String(creScope).toUpperCase()}`, value: number(metric.quality_failed), tone: 2 },
    ].filter((item) => item.value > 0);
  }
  return [
    { label: 'No Error', value: number(metric.quality_passed), tone: 3 },
    { label: 'Error / Other', value: number(metric.quality_failed), tone: 2 },
    { label: 'Blank', value: number(metric.quality_blank), tone: 1 },
  ].filter((item) => item.value > 0);
}

function MetricTable({ rows }) {
  if (!rows?.length) return <div className="scorecard-chart-empty">No primary milestone output for this role in the selected period.</div>;
  return (
    <div className="scorecard-table-scroll">
      <table className="scorecard-kpi-table">
        <thead>
          <tr>
            <th>Milestone Type</th>
            <th className="num">Output</th>
            <th className="num">Quality Passed</th>
            <th className="num">Quality Error / Failed</th>
            <th className="num">Quality Blank</th>
            <th className="num">Quality %</th>
            <th className="num">TAT Target</th>
            <th className="num">TAT On-Time</th>
            <th className="num">TAT Late</th>
            <th className="num">TAT On-Time %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.milestone_type}>
              <td>{row.milestone_type}</td>
              <td className="num">{number(row.output_count)}</td>
              <td className="num">{row.quality_applicable ? number(row.quality_passed) : 'N/A'}</td>
              <td className="num">{row.quality_applicable ? number(row.quality_failed) : 'N/A'}</td>
              <td className="num">{row.quality_applicable ? number(row.quality_blank) : 'N/A'}</td>
              <td className="num">{row.quality_applicable && row.quality_percent !== null && row.quality_percent !== undefined ? percent(row.quality_percent) : 'N/A'}</td>
              <td className="num">{row.tat_threshold_label || 'N/A'}</td>
              <td className="num">{number(row.tat_on_time)}</td>
              <td className="num">{number(row.tat_late)}</td>
              <td className="num">{percent(row.tat_on_time_percent)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusTable({ items }) {
  const total = items.reduce((sum, item) => sum + number(item.value), 0);
  return (
    <div className="scorecard-table-scroll scorecard-status-table-wrap">
      <table className="scorecard-kpi-table compact">
        <thead><tr><th>Status</th><th className="num">Project Count</th><th className="num">% of Total</th></tr></thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.label}>
              <td>{item.label}</td>
              <td className="num">{number(item.value)}</td>
              <td className="num">{total ? `${((number(item.value) / total) * 100).toFixed(1)}%` : '0.0%'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DistributionChart({ title, rows, nameKey }) {
  const items = (rows || []).map((row, index) => ({
    label: row[nameKey],
    value: number(row.project_count),
    tone: (index % 3) + 1,
  }));
  if (!items.length) return null;
  return <DonutChartCard title={title} items={items} />;
}

function RolePerformanceSection({ section }) {
  const metricRows = section.metricRows || [];
  const initialMetric = metricRows.find((row) => row.milestone_type === 'Initial Project Delivery');
  const restampMetric = metricRows.find((row) => row.milestone_type === 'Restamp Required');
  const creScope = section.key === 'design_review' ? 'design' : section.key === 'pe' ? 'pe' : section.key === 'ee' ? 'ee' : '';
  const initialQuality = toStatusItems(initialMetric, 'quality', creScope);
  const restampQuality = toStatusItems(restampMetric, 'quality', creScope);
  const initialTat = toStatusItems(initialMetric, 'tat');
  const restampTat = toStatusItems(restampMetric, 'tat');
  const outputItems = metricRows.map((row) => ({ label: row.milestone_type, value: number(row.output_count) }));
  const serviceItems = (section.serviceRows || []).map((row, index) => ({ label: row.service_type, value: number(row.project_count), tone: (index % 3) + 1 }));

  return (
    <section className="scorecard-kpi-section">
      <div className="scorecard-kpi-section__bar">
        <h2>{section.sectionTitle}</h2>
        <span>{number(section.assignmentCount)} assignment{number(section.assignmentCount) === 1 ? '' : 's'}</span>
      </div>

      <div className="scorecard-kpi-panel">
        <p><strong>Output:</strong> {section.outputRule}</p>
        <p><strong>Quality:</strong> {section.qualityRule}</p>
        <p><strong>TAT:</strong> Initial target = {thresholdLabel(metricRows, 'Initial Project Delivery')}; Restamp target = {thresholdLabel(metricRows, 'Restamp Required')} using {section.tatLabel}.</p>
        <MetricTable rows={metricRows} />
      </div>

      <div className="scorecard-kpi-grid-two">
        {serviceItems.length > 0 && <DonutChartCard title={`${section.label} — Service Type Mix`} items={serviceItems} />}
        {outputItems.length > 0 && <BarChartCard title={`${section.label} — Output by Milestone`} items={outputItems} />}
      </div>

      {(initialQuality.length > 0 || restampQuality.length > 0) && (
        <div className="scorecard-kpi-grid-two">
          {initialQuality.length > 0 && <div className="scorecard-kpi-panel scorecard-kpi-chart-table"><DonutChartCard title={`${section.label} — Initial Project Delivery: Quality`} items={initialQuality} /><StatusTable items={initialQuality} /></div>}
          {restampQuality.length > 0 && <div className="scorecard-kpi-panel scorecard-kpi-chart-table"><DonutChartCard title={`${section.label} — Restamp Required: Quality`} items={restampQuality} /><StatusTable items={restampQuality} /></div>}
        </div>
      )}

      {(initialTat.length > 0 || restampTat.length > 0) && (
        <div className="scorecard-kpi-grid-two">
          {initialTat.length > 0 && <div className="scorecard-kpi-panel scorecard-kpi-chart-table"><DonutChartCard title={`${section.label} — Initial Project Delivery: TAT On-Time vs Late`} items={initialTat} /><StatusTable items={initialTat} /></div>}
          {restampTat.length > 0 && <div className="scorecard-kpi-panel scorecard-kpi-chart-table"><DonutChartCard title={`${section.label} — Restamp Required: TAT On-Time vs Late`} items={restampTat} /><StatusTable items={restampTat} /></div>}
        </div>
      )}
    </section>
  );
}

function IdleTime({ rows }) {
  if (!rows?.length) return null;
  const max = Math.max(0, ...rows.flatMap((row) => [number(row.avg_ready_for_design), number(row.avg_ready_for_pe)]));
  return (
    <section className="scorecard-kpi-section">
      <div className="scorecard-kpi-section__bar"><h2>Average Idle Time</h2><span>Rows where the logged-in member appears in any role</span></div>
      <div className="scorecard-kpi-grid-two">
        <div className="scorecard-kpi-panel scorecard-table-scroll">
          <table className="scorecard-kpi-table">
            <thead><tr><th>Milestone Type</th><th className="num">Avg Ready for Design (hrs)</th><th className="num">Avg Ready for PE (hrs)</th><th className="num">Request Count</th></tr></thead>
            <tbody>{rows.map((row) => <tr key={row.milestone_type}><td>{row.milestone_type}</td><td className="num">{number(row.avg_ready_for_design).toFixed(2)}</td><td className="num">{number(row.avg_ready_for_pe).toFixed(2)}</td><td className="num">{number(row.request_count)}</td></tr>)}</tbody>
          </table>
        </div>
        <article className="scorecard-chart-card">
          <div className="scorecard-chart-card__header"><div><h2>Average Idle Time</h2></div></div>
          <div className="scorecard-grouped-bars">
            {rows.map((row) => (
              <div key={row.milestone_type} className="scorecard-grouped-bars__group">
                <strong>{row.milestone_type}</strong>
                <div className="scorecard-grouped-bars__series"><span>Ready for Design</span><div className="scorecard-bar-track"><div className="scorecard-bar-fill scorecard-tone-bg-1" style={{ width: `${max ? (number(row.avg_ready_for_design) / max) * 100 : 0}%` }} /></div><em>{number(row.avg_ready_for_design).toFixed(2)}h</em></div>
                <div className="scorecard-grouped-bars__series"><span>Ready for PE</span><div className="scorecard-bar-track"><div className="scorecard-bar-fill scorecard-tone-bg-3" style={{ width: `${max ? (number(row.avg_ready_for_pe) / max) * 100 : 0}%` }} /></div><em>{number(row.avg_ready_for_pe).toFixed(2)}h</em></div>
              </div>
            ))}
          </div>
        </article>
      </div>
    </section>
  );
}

function AssignmentDetail({ rows }) {
  if (!rows?.length) return null;
  return (
    <section className="scorecard-kpi-section">
      <div className="scorecard-kpi-section__bar"><h2>Assignment Detail</h2><span>Showing up to 300 rows</span></div>
      <div className="scorecard-kpi-panel scorecard-table-scroll">
        <table className="scorecard-kpi-table scorecard-assignment-detail-table">
          <thead><tr><th>Project</th><th>Date</th><th>Milestone Type</th><th>Assigned Role</th><th>Service Type</th><th className="num">TAT</th><th className="num">Target TAT</th><th>Milestone QA Result</th><th>CRE Restamp Reason</th><th>Role TAT Status</th></tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.project_name}</td><td>{row.milestone_date}</td><td>{row.milestone_type}</td><td>{row.assigned_role}</td><td>{row.service_type}</td>
                <td className="num">{row.tat_hours === null || row.tat_hours === undefined ? '—' : Number(row.tat_hours).toFixed(2)}</td>
                <td className="num">{row.target_tat_label || 'N/A'}</td><td>{row.qa_result || 'Not Set'}</td><td>{row.cre_restamp_reason || 'Not Set'}</td>
                <td><span className={`scorecard-status ${row.status === 'On-Time' ? 'scorecard-status--on-time' : row.status === 'Late' ? 'scorecard-status--late' : ''}`}>{row.status || 'N/A'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function IndividualPerformanceView({ data, employeeName, onExport }) {
  const topStats = data?.topStats || [];
  const kpiSummary = data?.kpiSummary || [];
  const milestoneMix = data?.milestoneMix || [];
  const serviceMix = data?.serviceMix || [];
  const roleSections = data?.roleSections || [];
  const idleRows = data?.idleRows || [];
  const assignments = data?.assignments || [];

  return (
    <div className="scorecard-individual-performance">
      <div className="scorecard-individual-title-row">
        <div>
          <h2>Individual Team Member Performance</h2>
          <p>Design, Design Review, PE, and EE output, quality, and TAT metrics for <strong>{employeeName}</strong>.</p>
        </div>
        <button type="button" className="scorecard-export-btn" onClick={onExport}>Export Excel</button>
      </div>

      {topStats.length > 0 && <div className="scorecard-metrics-grid scorecard-kpi-top-stats">{topStats.map((item) => <ScoreCardMetric key={item.key} label={item.label} value={statValue(item)} />)}</div>}

      {roleSections.length > 0 && (
        <section className="scorecard-kpi-section">
          <div className="scorecard-kpi-section__bar"><h2>KPI</h2><span>Output Quantity, Quality, and Turnaround Time</span></div>
          <div className="scorecard-metrics-grid scorecard-kpi-summary-grid">{kpiSummary.map((item) => <ScoreCardMetric key={item.key} label={item.label} value={statValue(item)} detail={item.helper} />)}</div>
        </section>
      )}

      {(milestoneMix.length > 0 || serviceMix.length > 0) && (
        <section className="scorecard-kpi-section">
          <div className="scorecard-kpi-section__bar"><h2>Overall Assignment Mix</h2><span>Unique milestone rows where {employeeName} appears in any role</span></div>
          <div className="scorecard-kpi-grid-two">
            <DistributionChart title="Milestone Type Mix" rows={milestoneMix} nameKey="milestone_type" />
            <DistributionChart title="Service Type Mix" rows={serviceMix} nameKey="service_type" />
          </div>
        </section>
      )}

      {roleSections.map((section) => <RolePerformanceSection key={section.key} section={section} />)}
      <IdleTime rows={idleRows} />
      <AssignmentDetail rows={assignments} />
    </div>
  );
}
